import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './BevelButton.module.css';

export type BevelTone = 'cyan' | 'amber' | 'gold' | 'lime' | 'rose' | 'violet';

interface Props {
  /** Icon node — typically a <NavIcon name="…"/> or an emoji span. */
  icon: ReactNode;
  /** Bold label rendered first in the projected popover. */
  label: string;
  /** One-liner description rendered as the tile-context line. */
  blurb?: string;
  /** Renders the active-state ring + glow (currently-routed page). */
  active?: boolean;
  /** Tone-tints the active glow. Default cyan. */
  tone?: BevelTone;
  /** Side the popover projects toward — 'right' for the left rail,
   *  'left' for the right rail. */
  popoverSide?: 'left' | 'right';
  /** Soft tinted background for persona-highlighted items. */
  personaTint?: string;
  /** Click handler. */
  onClick: () => void;
  /** A11y label — falls back to `label` when omitted. */
  ariaLabel?: string;
  /** When true, sets aria-current="page" — used for active route. */
  current?: boolean;
}

/**
 * Apple-style bevel button — a 44×44 gel/embossed surface that rises
 * on hover and projects a glass popover with the icon name + a single
 * one-liner readout of what the destination does.
 *
 * The popover renders through `createPortal` to `document.body` so it
 * escapes the rail's `overflow-y: auto` clip and floats above all
 * other shell chrome. Position is recomputed from the trigger
 * button's bounding rect each time the popover opens; scrolling or
 * resizing closes it so it doesn't lag behind the rail.
 *
 * Reduced-motion: the popover crossfades only — no slide animation.
 */
export default function BevelButton({
  icon,
  label,
  blurb,
  active,
  tone = 'cyan',
  popoverSide = 'right',
  personaTint,
  onClick,
  ariaLabel,
  current,
}: Props) {
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number; right: number } | null>(null);

  // Compute the portal popover's anchor before paint to avoid a flash
  // at (0, 0).
  useLayoutEffect(() => {
    if (!popoverOpen || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPopoverPos({
      top: r.top + r.height / 2,
      left: r.right + 14,
      right: window.innerWidth - r.left + 14,
    });
  }, [popoverOpen]);

  // Close popover on scroll/resize so it doesn't lag the rail. capture:true
  // catches scrolls inside any nested scroll container.
  useEffect(() => {
    if (!popoverOpen) return;
    const close = () => setPopoverOpen(false);
    window.addEventListener('scroll', close, { passive: true, capture: true });
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, { capture: true });
      window.removeEventListener('resize', close);
    };
  }, [popoverOpen]);

  const style: CSSProperties | undefined = personaTint
    ? ({ ['--bevel-persona-tint' as string]: personaTint } as CSSProperties)
    : undefined;

  return (
    <div
      className={styles.wrap}
      onMouseEnter={() => setPopoverOpen(true)}
      onMouseLeave={() => setPopoverOpen(false)}
      onFocus={() => setPopoverOpen(true)}
      onBlur={() => setPopoverOpen(false)}
    >
      <button
        ref={btnRef}
        type="button"
        className={`${styles.btn} ${styles[`tone_${tone}`]} ${active ? styles.active : ''} ${personaTint ? styles.personaTinted : ''}`}
        onClick={onClick}
        aria-label={ariaLabel ?? label}
        aria-current={current ? 'page' : undefined}
        style={style}
      >
        <span className={styles.iconWrap} aria-hidden="true">{icon}</span>
      </button>
      {popoverOpen && popoverPos && createPortal(
        <div
          className={`${styles.popover} ${popoverSide === 'left' ? styles.popoverLeft : styles.popoverRight} ${styles[`popoverTone_${tone}`]}`}
          role="tooltip"
          style={
            popoverSide === 'right'
              ? { top: popoverPos.top, left: popoverPos.left }
              : { top: popoverPos.top, right: popoverPos.right }
          }
        >
          <div className={styles.popoverLabel}>{label}</div>
          {blurb ? (
            <div className={styles.popoverBlurb}>
              <span className={styles.popoverBlurbText}>{blurb}</span>
            </div>
          ) : null}
        </div>,
        document.body,
      )}
    </div>
  );
}
